import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Settings, Clock, Users, Calendar, Bell, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { settingsApi } from '../../services/api';

interface ShiftPlanningSettingsData {
  defaultShiftDuration: number;
  minShiftDuration: number;
  maxShiftDuration: number;
  minBreakDuration: number;
  autoApproveSwaps: boolean;
  requireManagerApproval: boolean;
  allowSelfAssignment: boolean;
  sendNotifications: boolean;
  notifyBeforeShift: number;
  maxConsecutiveDays: number;
  minRestHours: number;
}

const defaultSettings: ShiftPlanningSettingsData = {
  defaultShiftDuration: 8,
  minShiftDuration: 4,
  maxShiftDuration: 12,
  minBreakDuration: 30,
  autoApproveSwaps: false,
  requireManagerApproval: true,
  allowSelfAssignment: false,
  sendNotifications: true,
  notifyBeforeShift: 24,
  maxConsecutiveDays: 6,
  minRestHours: 12,
};

const ShiftPlanningSettings: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<ShiftPlanningSettingsData>(defaultSettings);

  // Fetch settings from API
  const { data: savedSettings, isLoading } = useQuery({
    queryKey: ['shiftPlanningSettings'],
    queryFn: () => settingsApi.shiftPlanning.get(),
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (savedSettings) {
      setSettings({
        defaultShiftDuration: savedSettings.defaultShiftDuration ?? defaultSettings.defaultShiftDuration,
        minShiftDuration: savedSettings.minShiftDuration ?? defaultSettings.minShiftDuration,
        maxShiftDuration: savedSettings.maxShiftDuration ?? defaultSettings.maxShiftDuration,
        minBreakDuration: savedSettings.minBreakDuration ?? defaultSettings.minBreakDuration,
        autoApproveSwaps: savedSettings.autoApproveSwaps ?? defaultSettings.autoApproveSwaps,
        requireManagerApproval: savedSettings.requireManagerApproval ?? defaultSettings.requireManagerApproval,
        allowSelfAssignment: savedSettings.allowSelfAssignment ?? defaultSettings.allowSelfAssignment,
        sendNotifications: savedSettings.sendNotifications ?? defaultSettings.sendNotifications,
        notifyBeforeShift: savedSettings.notifyBeforeShift ?? defaultSettings.notifyBeforeShift,
        maxConsecutiveDays: savedSettings.maxConsecutiveDays ?? defaultSettings.maxConsecutiveDays,
        minRestHours: savedSettings.minRestHours ?? defaultSettings.minRestHours,
      });
    }
  }, [savedSettings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: ShiftPlanningSettingsData) => settingsApi.shiftPlanning.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftPlanningSettings'] });
      toast({ title: 'Settings saved successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error saving settings', description: error.message, variant: 'destructive' });
    },
  });

  const handleSave = () => {
    saveMutation.mutate(settings);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configure shift planning rules and preferences</h1>
       
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Settings className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Shift Duration Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="defaultDuration">Default Duration (hours)</Label>
                  <Input 
                    id="defaultDuration" 
                    type="number" 
                    value={settings.defaultShiftDuration} 
                    onChange={(e) => setSettings({ ...settings, defaultShiftDuration: parseInt(e.target.value) || 0 })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="minDuration">Minimum Duration (hours)</Label>
                  <Input 
                    id="minDuration" 
                    type="number" 
                    value={settings.minShiftDuration} 
                    onChange={(e) => setSettings({ ...settings, minShiftDuration: parseInt(e.target.value) || 0 })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxDuration">Maximum Duration (hours)</Label>
                  <Input 
                    id="maxDuration" 
                    type="number" 
                    value={settings.maxShiftDuration} 
                    onChange={(e) => setSettings({ ...settings, maxShiftDuration: parseInt(e.target.value) || 0 })} 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="breakDuration">Minimum Break Duration (minutes)</Label>
                <Input 
                  id="breakDuration" 
                  type="number" 
                  value={settings.minBreakDuration} 
                  onChange={(e) => setSettings({ ...settings, minBreakDuration: parseInt(e.target.value) || 0 })} 
                  className="max-w-xs" 
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Assignment Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="requireApproval">Require Manager Approval</Label>
                  <p className="text-sm text-muted-foreground">Shift assignments need manager approval</p>
                </div>
                <Switch 
                  id="requireApproval" 
                  checked={settings.requireManagerApproval} 
                  onCheckedChange={(checked) => setSettings({ ...settings, requireManagerApproval: checked })} 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="selfAssignment">Allow Self-Assignment</Label>
                  <p className="text-sm text-muted-foreground">Employees can pick their own shifts</p>
                </div>
                <Switch 
                  id="selfAssignment" 
                  checked={settings.allowSelfAssignment} 
                  onCheckedChange={(checked) => setSettings({ ...settings, allowSelfAssignment: checked })} 
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Work Rules</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="maxConsecutive">Maximum Consecutive Work Days</Label>
                <Input 
                  id="maxConsecutive" 
                  type="number" 
                  value={settings.maxConsecutiveDays} 
                  onChange={(e) => setSettings({ ...settings, maxConsecutiveDays: parseInt(e.target.value) || 0 })} 
                  className="max-w-xs" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="minRest">Minimum Rest Hours Between Shifts</Label>
                <Input 
                  id="minRest" 
                  type="number" 
                  value={settings.minRestHours} 
                  onChange={(e) => setSettings({ ...settings, minRestHours: parseInt(e.target.value) || 0 })} 
                  className="max-w-xs" 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="autoApprove">Auto-Approve Shift Swaps</Label>
                  <p className="text-sm text-muted-foreground">Automatically approve shift swap requests</p>
                </div>
                <Switch 
                  id="autoApprove" 
                  checked={settings.autoApproveSwaps} 
                  onCheckedChange={(checked) => setSettings({ ...settings, autoApproveSwaps: checked })} 
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5" />
                <span>Notification Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sendNotifications">Send Notifications</Label>
                  <p className="text-sm text-muted-foreground">Enable shift notifications</p>
                </div>
                <Switch 
                  id="sendNotifications" 
                  checked={settings.sendNotifications} 
                  onCheckedChange={(checked) => setSettings({ ...settings, sendNotifications: checked })} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notifyBefore">Notify Before Shift (hours)</Label>
                <Input 
                  id="notifyBefore" 
                  type="number" 
                  value={settings.notifyBeforeShift} 
                  onChange={(e) => setSettings({ ...settings, notifyBeforeShift: parseInt(e.target.value) || 0 })} 
                  className="max-w-xs" 
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ShiftPlanningSettings;
